// CORE IMPORTS
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, useTheme, useMediaQuery } from "@mui/system";
import React from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import CryptoSelectionModalV2 from "../targets/components/CryptoSelectionModalV2";

// REDUX
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";
import {
  setBuyAssetSide,
  setSellAssetSide,
  useTargetStateV2,
} from "src/store/features/TargetOrdersV2.slice";
import { useUserSlice } from "src/store/features/user.slice";

// TYPES
import { TradeAction, TradeType } from "../types";
import { ICurrency } from "../targets/components/CryptoSelectionModalV2";

// HOOKS
import { useCurrencyData } from "src/hooks/useCurrencyData";

export const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  position: "relative",
}));

export const ButtonText = styled("div")(({ theme }) => ({
  fontSize: "18px",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
  },
}));

export const MaxText = styled("div")(() => ({
  position: "absolute",
  top: 45,
  width: "200px",
  fontSize: "14px !important",
  textAlign: "right",
  paddingRight: "10px",
  zIndex: 99,
  whiteSpace: "nowrap",
}));

type TokenType = "all" | "crypto" | "fiat";

interface TradeCoinSelectionButtonProps {
  code: string;
  logoURL: string;
  maxTotal: string;
  action: TradeAction;
  tradeType: TradeType;
  tokenType?: TokenType;
}

const TradeCoinSelectionButton = ({
  code,
  logoURL,
  maxTotal,
  action,
  tradeType,
  tokenType = "all",
}: TradeCoinSelectionButtonProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const buyCodeURL = searchParams.get("code");

  const { sellAssetSide, buyAssetSide } = useTargetStateV2();

  const {
    defaultCurrency,
    details: { customerId },
  } = useUserSlice();
  const { code: userBaseCurrency } = defaultCurrency;

  // user id ref
  const currencyUserID = React.useRef<string>(customerId);

  // memoized currency data
  const currencyData = useCurrencyData(userBaseCurrency);

  const [open, setOpen] = React.useState<boolean>(false);

  const handleOpenCryptoSelectionModal = (): void => {
    setOpen(true);
  };

  const handleCloseCryptoSelectionModal = (): void => {
    setOpen(false);
  };

  const handleErrorSide = (): boolean => {
    if (action === "sell") return currencyData.isHoldingsError;
    return currencyData.isCryptoAllError || currencyData.isFiatError;
  };

  const getLoadingSide = (): boolean => {
    if (action === "sell") return currencyData.isHoldingsLoading || currencyData.isHoldingsFetching;
    return currencyData.isCryptoAllLoading || currencyData.isFiatLoading;
  };

  /**
   * Memoized currency mapping based on trade action and type
   *
   * - Swap:
   *   - Sell side shows user's holdings
   *   - Buy side shows all available currencies with user holdings included
   *
   * - Target:
   *   - For sell action:
   *     - On the sell side, show user's crypto holdings without stablecoins
   *     - On the buy side, show all fiat and stable coins with user holdings included
   *   - For buy action:
   *     - On the buy side, show all cryptocurrencies without stables with user holdings included
   *     - On the sell side, show the users fiat and stable holdings
   *
   * - Scheduled:
   *   - For sell action:
   *     - On the sell side, show user's fiat holdings
   *     - On the buy side, show all cryptocurrencies without stables with user holdings included
   */
  const getCurrencyData = (): ICurrency[] => {
    type CurrencyDataMap = {
      [key in TradeAction]: {
        [key in TradeType]: {
          [key in TokenType]?: ICurrency[];
        };
      };
    };

    const currencyDataMap: CurrencyDataMap = {
      sell: {
        swap: { all: currencyData.allUserHoldings },
        target: {
          all: currencyData.userCryptoHoldingsWithoutStables,
          crypto: currencyData.userFiatAndStableHoldings,
        },
        scheduled: { all: currencyData.usersFiatHoldings },
      },
      buy: {
        swap: { all: currencyData.allCurrenciesWithUserHoldings },
        target: {
          all: currencyData.fiatAndStablesWithUserHoldings,
          crypto: currencyData.cryptoNoStablesOrFiatWithUserHoldings,
        },
        scheduled: { all: currencyData.cryptoNoStablesOrFiatWithUserHoldings },
      },
    };

    const tokenKey = currencyDataMap[action]?.[tradeType]?.[tokenType] ? tokenType : "all";
    return currencyDataMap[action]?.[tradeType]?.[tokenKey] || [];
  };

  React.useEffect((): void => {
    if (!currencyData.allCurrenciesWithUserHoldings) return;
    if (buyCodeURL) {
      const currency = getCurrencyData().find(
        (currency: ICurrency): boolean => currency.code.toLowerCase() === buyCodeURL.toLowerCase(),
      );

      if (currency) {
        const { code, logoURL, units, price } = currency;

        const actionMap: Record<string, Record<TradeType, () => any>> = {
          sell: {
            swap: () => {
              dispatch(setSellInput({ code, logoURL, maxTotal: units, price }));
            },
            target: () => {
              if (action === "sell") {
                dispatch(
                  setSellAssetSide({ ...sellAssetSide, code, logoURL, maxTotal: units, price }),
                );
              } else {
                dispatch(
                  setBuyAssetSide({ ...buyAssetSide, code, logoURL, maxTotal: units, price }),
                );
              }
            },
            scheduled: () => {},
          },
          buy: {
            swap: () => {
              dispatch(setBuyInput({ code, logoURL, maxTotal: units, price }));
            },
            target: () => {
              if (action === "buy") {
                dispatch(
                  setBuyAssetSide({ ...buyAssetSide, code, logoURL, maxTotal: units, price }),
                );
              } else {
                dispatch(
                  setSellAssetSide({ ...sellAssetSide, code, logoURL, maxTotal: units, price }),
                );
              }
            },
            scheduled: () => {},
          },
        };

        const dispatchAction = actionMap[action]?.[tradeType ?? "swap"];

        if (dispatchAction) {
          dispatchAction();
        } else {
          console.error("Invalid action or trade type");
        }
      }
    }
  }, [buyCodeURL, currencyData.allCurrenciesWithUserHoldings]);

  React.useEffect((): void => {
    // user changed user, so refresh rtkq and fetch again
    if (currencyUserID.current !== customerId) {
      currencyUserID.current = customerId;
      currencyData.refetchWallet();
    }
  }, []);

  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));

  const btnWidth = code !== "Select" && code.length > 3 ? "125px" : isBelowMd ? "95px" : "115px";
  const btnHeight = isBelowMd ? "35px" : "46px";
  const btnBorderRadius = isBelowMd ? "14px" : "20px";

  return (
    <Wrapper>
      <ContainedButton
        id={`trade-coin-selection-button-${action}`}
        color={theme.palette.mode === "light" ? "navy" : "dark"}
        borderRadius={btnBorderRadius}
        sx={{
          boxShadow: "0px 4px 16px 0px rgba(42, 95, 129, 0.30);",
          fontSize: "18px",
          lineHeight: "22px",
        }}
        width={btnWidth}
        height={btnHeight}
        startIcon={
          code !== "Select" ? <CountryOrCryptoFlag name={logoURL || code} size={20} /> : undefined
        }
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpenCryptoSelectionModal}>
        <ButtonText>{code}</ButtonText>
      </ContainedButton>
      <CryptoSelectionModalV2
        open={open}
        onClose={handleCloseCryptoSelectionModal}
        currencies={getCurrencyData()}
        hasError={handleErrorSide()}
        isLoading={getLoadingSide()}
        action={action}
        tradeType={tradeType}
      />
    </Wrapper>
  );
};

export default TradeCoinSelectionButton;
